import React from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ImportFileEntry, ImportFileType } from '../types';
import {
  getColumnMappingOrder,
  getColumnMappingIcons,
  getMissingRequiredColumns,
  COLUMN_CONFIGS,
} from '../utils/columnMappingConfig';

interface ColumnMappingStepProps {
  files: ImportFileEntry[];
  activeFileIndex: number;
  onActiveFileIndexChange: (index: number) => void;
  onSetColumnMapping: (fileId: string, mapping: Record<string, string>) => void;
}

function getMappingStatusColor(file: ImportFileEntry): 'success' | 'warning' | 'error' | 'default' {
  if (file.mappingStatus === 'not-required') return 'success';
  if (file.parseError) return 'error';
  const missing = getMissingRequiredColumns(file.fileType, file.columnMapping);
  if (missing.length > 0) return 'error';
  return 'success';
}

const ColumnMappingStep: React.FC<ColumnMappingStepProps> = ({
  files,
  activeFileIndex,
  onActiveFileIndexChange,
  onSetColumnMapping,
}) => {
  const { t } = useTranslation('mapDialogs');
  const icons = getColumnMappingIcons();

  const tabularFiles = files.filter(f => f.mappingStatus !== 'not-required' && !f.parseError);
  const gtfsFiles = files.filter(f => f.mappingStatus === 'not-required');

  if (tabularFiles.length === 0 && gtfsFiles.length > 0) {
    return (
      <Alert severity="info">
        {t('importWizard.columnMappingStep.noMappingNeeded')}
      </Alert>
    );
  }

  const activeFile = tabularFiles[activeFileIndex] ?? tabularFiles[0];
  if (!activeFile) return null;

  const mappingOrder = getColumnMappingOrder(activeFile.fileType);
  const columnConfig = COLUMN_CONFIGS[activeFile.fileType];
  const missingRequired = getMissingRequiredColumns(activeFile.fileType, activeFile.columnMapping);

  return (
    <Stack spacing={3}>
      {tabularFiles.length > 1 && (
        <Tabs
          value={activeFileIndex}
          onChange={(_, newVal) => onActiveFileIndexChange(newVal)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabularFiles.map((file, idx) => (
            <Tab
              key={file.id}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{file.fileName}</span>
                  <Chip
                    size="small"
                    color={getMappingStatusColor(file)}
                    label={
                      getMissingRequiredColumns(file.fileType, file.columnMapping).length > 0
                        ? t('importWizard.columnMappingStep.incomplete')
                        : t('importWizard.columnMappingStep.ready')
                    }
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Stack>
              }
            />
          ))}
        </Tabs>
      )}

      {missingRequired.length > 0 && (
        <Alert severity="warning">
          {t('importWizard.columnMappingStep.missingRequired', {
            columns: missingRequired.map(key => {
              const cfg = columnConfig.find(c => c.key === key);
              return cfg ? t(`importWizard.columnLabels.${cfg.labelKey}`) : key;
            }).join(', '),
          })}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        {t('importWizard.columnMappingStep.instruction')}
      </Typography>

      <Stack spacing={1.5}>
        {mappingOrder.map((row, rowIndex) => (
          <Box
            key={`mapping-row-${rowIndex}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: `repeat(${row.length}, minmax(0, 1fr))` },
              gap: 1.5,
            }}
          >
            {row.map(key => {
              const cfg = columnConfig.find(c => c.key === key);
              const isRequired = cfg?.required ?? false;
              const isMissing = isRequired && !activeFile.columnMapping[key];
              const label = t(`importWizard.columnLabels.${cfg?.labelKey ?? key}`);

              return (
                <FormControl
                  key={key}
                  fullWidth
                  error={isMissing}
                >
                  <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {icons[key]}
                    <span>{label}{isRequired ? ' *' : ''}</span>
                  </InputLabel>
                  <Select
                    value={activeFile.columnMapping[key] || ''}
                    label={label}
                    onChange={e => {
                      onSetColumnMapping(activeFile.id, {
                        ...activeFile.columnMapping,
                        [key]: e.target.value as string,
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('importWizard.columnMappingStep.none')}</em>
                    </MenuItem>
                    {activeFile.parsedColumns.map(col => (
                      <MenuItem key={col} value={col}>{col}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            })}
          </Box>
        ))}
      </Stack>

      {gtfsFiles.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('importWizard.columnMappingStep.gtfsSkipped', { count: gtfsFiles.length })}
        </Alert>
      )}
    </Stack>
  );
};

export default ColumnMappingStep;
